/**
 * Prioritization Templates for Top Priority App
 * 
 * Pre-configured templates for common prioritization scenarios.
 * Each template includes criteria, example projects, scores, and a realistic dilemma.
 */

export const prioritizationTemplates = {
	"weekly-sprint": {
		name: "Weekly Sprint Planning",
		description: "Focus on what you can accomplish this week",
		criteria: [
			"Expected Impact",
			"Effort Required", 
			"Time Sensitivity",
			"Momentum",
		],
		// Chaotic brain dump from a stressed executive
		dilemma: `Board meeting Friday and investor deck is NOWHERE near ready. Sarah keeps asking about the developer hire - told her "this week" three weeks ago 🙈. Payment system throwing errors again (Marcus from Acme Corp emailed AGAIN, all caps this time). Haven't done 1:1s in two weeks and I can feel the team getting weird. Also promised my wife I'd be home for dinner at least twice this week... does that count as a task? WHERE DO I EVEN START`,
		// Example projects for ambitious professionals
		projects: [
			"Finish Investor Deck",
			"Hire Senior Developer", 
			"Fix Payment Bug",
			"Team 1:1 Meetings"
		],
		// Scores for each project across criteria [Impact, Effort, TimeSensitivity, Momentum]
		// Note: "Effort Required" has polarity "Lower is better", so LOW score = LOW effort (good)
		scores: {
			"Finish Investor Deck":    [90, 70, 95, 40],  // High impact, HIGH effort, very urgent, stuck
			"Hire Senior Developer":   [75, 80, 45, 20],  // High impact, HIGH effort, not urgent, very stuck
			"Fix Payment Bug":         [55, 15, 85, 95],  // Medium impact, LOW effort, urgent, ready to go
			"Team 1:1 Meetings":       [50, 20, 35, 75],  // Medium impact, LOW effort, not urgent, easy to do
		},
		example: "Plan your most important tasks for the next 7 days", // Fallback
	},
	"quarterly-goals": {
		name: "Quarterly Goal Setting", 
		description: "Prioritize major initiatives for the quarter",
		criteria: [
			"Strategic Alignment",
			"Expected Impact",
			"Dependencies",
			"Expected Return on Investment (ROI)",
		],
		dilemma: `Q1 planning and the board wants our "top 3 priorities" by Monday. Meanwhile we have like 12 initiatives floating around. Marketing wants to rebrand AGAIN. Engineering is begging to fix technical debt before we "hit a wall." Sales promised a client we'd have the mobile app by March?? And everyone keeps talking about "AI" like it's magic beans. Also our competitor just launched in Europe and now the CEO is obsessed with international expansion. I need to make sense of this mess before I lose my mind 🤯`,
		projects: [
			"Launch Mobile App",
			"EU Market Expansion",
			"Technical Debt Sprint",
			"AI Features Pilot"
		],
		scores: {
			"Launch Mobile App":      [75, 80, 70, 85],  // Good alignment, high impact, some dependencies, strong ROI
			"EU Market Expansion":    [90, 95, 45, 75],  // Very strategic, huge impact, regulatory blockers, good ROI
			"Technical Debt Sprint":  [50, 40, 90, 60],  // Less strategic, enables others, few dependencies, efficiency ROI
			"AI Features Pilot":      [85, 70, 55, 50],  // Trendy & strategic, uncertain impact, needs data team, unclear ROI
		},
		example: "Choose 3-5 major goals to focus on this quarter",
	},
	"daily-tasks": {
		name: "Daily Task Prioritization",
		description: "Organize your day for maximum productivity", 
		criteria: [
			"Time Sensitivity",
			"Expected Impact",
			"Effort Required",
			"Motivation / Excitement",
		],
		dilemma: `It's 8:47am and I'm already behind. Client proposal due at 5pm (haven't started the pricing section). 147 unread emails. Team standup in 2 hours and I have nothing prepared. Boss pinged me "quick question" which we all know means 45 minutes. Really wanted to work on that strategy doc today but WHO AM I KIDDING. Also told myself I'd go to the gym at lunch... that's definitely not happening is it 😅`,
		projects: [
			"Client Proposal (Due 5PM)",
			"Process Email Backlog",
			"Prep Team Standup",
			"Strategy Document"
		],
		// Scores: [TimeSensitivity, Impact, Effort, Motivation]
		// Note: "Effort Required" has polarity "Lower is better", so LOW score = LOW effort (good)
		scores: {
			"Client Proposal (Due 5PM)": [95, 90, 75, 30],  // Very urgent, high impact, HIGH effort, dreading it
			"Process Email Backlog":     [60, 35, 25, 25],  // Somewhat urgent, low impact, LOW effort, soul-crushing
			"Prep Team Standup":         [85, 55, 20, 50],  // Time-bound, medium impact, LOW effort, neutral
			"Strategy Document":         [20, 85, 70, 90],  // Not urgent, high impact, HIGH effort, excited about it
		},
		example: "Prioritize today's tasks by importance and urgency",
	},
	"project-portfolio": {
		name: "Project Portfolio Review",
		description: "Evaluate and prioritize multiple ongoing projects",
		criteria: [
			"Expected Return on Investment (ROI)",
			"Risk Reduction",
			"Strategic Alignment", 
			"Cost",
		],
		dilemma: `Board meeting next week and they want to know which projects to kill. We're running 6 major initiatives with budget for maybe 4. The AI chatbot everyone's hyped about has burned through $200k with "promising results" (whatever that means). Legacy migration is boring but engineering says we're "one incident away from disaster." Sales dashboard project is almost done but sales team now says they want something completely different 🙄. And that market research we commissioned? Still waiting on results but it might change everything...`,
		projects: [
			"AI Customer Service Bot",
			"Legacy System Migration",
			"Sales Dashboard v2",
			"Market Expansion Research"
		],
		// Scores: [ROI, RiskReduction, StrategicAlignment, Cost]
		// Note: "Cost" has polarity "Lower is better", so LOW score = LOW cost (good/cheap)
		scores: {
			"AI Customer Service Bot":    [70, 45, 85, 75],  // Promising ROI, some risk, very aligned, EXPENSIVE
			"Legacy System Migration":   [55, 95, 60, 70],  // Moderate ROI, huge risk reduction, okay alignment, EXPENSIVE
			"Sales Dashboard v2":        [80, 30, 55, 15],  // Quick ROI, low risk impact, medium alignment, CHEAP
			"Market Expansion Research": [50, 75, 90, 30],  // Uncertain ROI, reduces strategic risk, very aligned, moderate cost
		},
		example: "Decide which projects deserve your continued investment",
	},
	"personal-goals": {
		name: "Personal Development",
		description: "Balance life goals and personal growth",
		criteria: [
			"Learning Value",
			"Motivation / Excitement",
			"Strategic Alignment",
			"Social/Relationship Impact",
		],
		dilemma: `New year energy is fading and I still haven't figured out what to actually focus on. Want to learn Spanish (been saying this for 3 years). Should probably get that MBA everyone keeps mentioning. Been meaning to write more - maybe start a newsletter? And I really need to get back in shape, doc wasn't thrilled with my last checkup. Also promised my kids I'd coach their soccer team but that's every Saturday morning... when exactly am I supposed to do all this? Need to pick my battles here 🎯`,
		projects: [
			"Learn Spanish",
			"Start Health Routine",
			"Launch Newsletter",
			"Coach Kids' Soccer"
		],
		scores: {
			"Learn Spanish":        [85, 70, 65, 60],  // High learning, moderate excitement, career boost, some social
			"Start Health Routine": [40, 45, 80, 55],  // Low learning, hard to stay excited, very strategic, family benefits
			"Launch Newsletter":    [75, 90, 85, 70],  // Good learning, very exciting, builds brand, grows network
			"Coach Kids' Soccer":   [35, 75, 40, 95],  // Low learning, fun, not career-aligned, huge family impact
		},
		example: "Choose personal projects that align with your current life phase",
	},
};

/**
 * Color schemes for charts
 */
export const chartColors = [
	"rgba(156,255,250,0.8)", // Turquoise
	"rgba(8,75,131,0.8)",    // Navy
	"rgba(255,206,86,0.8)",  // Yellow
	"rgba(255,99,132,0.8)",  // Red
];

/**
 * Results chart color scheme (teal gradient for top 3, gray for rest)
 */
export const resultsChartColors = [
	"rgba(156,255,250,1.0)", // teal - top priority (full opacity)
	"rgba(156,255,250,0.7)", // teal - second priority
	"rgba(156,255,250,0.7)", // teal - third priority
	"rgba(150,150,150,0.5)", // gray - lower priority
	"rgba(150,150,150,0.5)", // gray - lowest priority
	"rgba(150,150,150,0.5)", // gray - even lower
];

/**
 * Default focus factors for new users
 */
export const defaultFocusFactors = [
	{
		name: "Impact on Goals",
		polarity: "Higher is better",
		category: "Critical",
		icon: "/Icons/target.svg",
	},
	{
		name: "Time Required",
		polarity: "Lower is better",
		category: "Critical",
		icon: "/Icons/clock.svg",
	},
	{
		name: "Urgency",
		polarity: "Higher is better",
		category: "Important",
		icon: "/Icons/urgency.svg",
	},
	{
		name: "Resource Availability",
		polarity: "Higher is better",
		category: "Important",
		icon: "/Icons/resources.svg",
	},
];
